import { useEffect, useRef, useState } from "react";
import { ChevronDown, Check, Building2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

export default function CompanySwitcher({ onSwitch, refreshKey = 0 }) {
  const [companies, setCompanies] = useState([]);
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const ref = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/portal/companies");
      setCompanies(data.companies || []);
      setActive(data.active_id || null);
    } catch {}
  };

  useEffect(() => {
    load();
  }, [refreshKey]);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const doSwitch = async (id) => {
    if (id === active || switching) {
      setOpen(false);
      return;
    }
    setSwitching(true);
    try {
      await api.post("/portal/switch-company", { client_id: id });
      setActive(id);
      setOpen(false);
      const target = companies.find((c) => c.id === id);
      toast.success(`Ahora estás viendo ${target?.company || target?.name}`);
      onSwitch?.(id);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo cambiar de empresa");
    } finally {
      setSwitching(false);
    }
  };

  if (!companies.length) return null;
  const activeMeta = companies.find((c) => c.id === active) || companies[0];

  // Single company: static badge, no dropdown
  if (companies.length === 1) {
    return (
      <div
        className="hidden md:inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-zinc-400 px-3 py-2 rounded-full border border-white/10"
        data-testid="company-switcher-static"
      >
        <Building2 className="w-3.5 h-3.5 text-[#D4AF37]" />
        {activeMeta.company || activeMeta.name}
      </div>
    );
  }

  return (
    <div className="relative" ref={ref} data-testid="company-switcher">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="company-switcher-toggle"
        className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-full border border-[#D4AF37]/40 hover:border-[#D4AF37] transition-colors bg-[#D4AF37]/5"
      >
        <Building2 className="w-4 h-4 text-[#D4AF37]" />
        <span className="text-white max-w-[180px] truncate">
          {activeMeta.company || activeMeta.name}
        </span>
        <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 w-72 bg-[#121214] border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50"
          data-testid="company-switcher-menu"
        >
          <div className="px-4 py-3 border-b border-white/5">
            <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">Cambiar de empresa</div>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {companies.map((c) => (
              <button
                key={c.id}
                onClick={() => doSwitch(c.id)}
                data-testid={`company-option-${c.id}`}
                className={`w-full text-left px-4 py-3 flex items-start justify-between gap-3 hover:bg-white/5 transition-colors ${
                  c.id === active ? "bg-[#D4AF37]/5" : ""
                }`}
              >
                <div className="min-w-0">
                  <div className="text-white text-sm truncate">
                    {c.company || c.name}
                    {c.is_primary && (
                      <span className="ml-2 text-[10px] uppercase tracking-widest text-zinc-500">principal</span>
                    )}
                  </div>
                  <div className="text-xs text-zinc-500 truncate font-mono">{c.email}</div>
                </div>
                {c.id === active && <Check className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
